// Dashboard del módulo Contabilidad. Contabilidad → Resumen → Dashboard.
// RPC cont_dashboard (script 216). Paleta categórica validada (dataviz):
// teal #0E9488 (ingresos) / coral #E05A3A (gastos) sobre superficie clara.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  Activity, AlertTriangle, BookText, Building2, CalendarRange, CheckCircle2, Landmark, Layers,
  Loader2, Scale, TrendingDown, TrendingUp, Wallet,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useTabsStore } from '@/stores/tabs.store';
import { useThemeStore } from '@/stores/theme.store';
import { ESCALAS, MODULO_LABEL, field, notaDeEscala, rpc, usd, usdEsc } from './shared';

const TEAL = '#0E9488';
const CORAL = '#E05A3A';
const STARK_TEAL = '#22d3ee';
const STARK_CORAL = '#fb7185';
const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

interface DashData {
  anio: number;
  situacion: { activos: number; pasivos: number; patrimonio: number; resultado: number };
  resultado: { ingresos_mes: number; gastos_mes: number; ingresos_anio: number; gastos_anio: number };
  asientos: { mes: number; anio: number; por_modulo: { modulo: string; n: number }[] };
  ejercicio: { anio: number; estado: string; periodos_cerrados: number; periodos_abiertos: number };
  serie_mensual: { mes: number; ingresos: number; gastos: number }[];
  top_gastos: { codigo: string; nombre: string; monto: number }[];
  pendientes: { ventas: number; compras: number; retenciones: number; inventario: number; errores_contabilizacion?: number };
}

const compact = (v: number) => {
  const a = Math.abs(v);
  if (a >= 1000) return `${(v / 1000).toFixed(1)}k`;
  return v.toFixed(0);
};

function Kpi({ icon, label, valor, detalle, clase }: { icon: React.ReactNode; label: string; valor: string; detalle?: string; clase?: string }) {
  return (
    <Card><CardContent className="py-3 flex items-start gap-3">
      <div className="text-primary mt-0.5">{icon}</div>
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className={`text-lg font-semibold font-mono tabular-nums truncate ${clase ?? ''}`}>{valor}</div>
        {detalle && <div className="text-xs text-muted-foreground">{detalle}</div>}
      </div>
    </CardContent></Card>
  );
}

export function DashboardContabilidad() {
  const isStark = useThemeStore((st) => st.currentThemeId === 'stark-hud');
  const cIng = isStark ? STARK_TEAL : TEAL;
  const cGas = isStark ? STARK_CORAL : CORAL;
  const openTab = useTabsStore((s) => s.openTab);
  const ir = (path: string, title: string) => openTab({ id: path, title, path });

  const [escala, setEscala] = useState(1);
  const nota = notaDeEscala(escala);
  // atajo local: todos los importes del tablero pasan por aquí
  const $ = (v: number | string | null | undefined) => usdEsc(v, escala);

  const { data, isFetching } = useQuery<DashData>({ queryKey: ['cont-dashboard'], queryFn: () => rpc<DashData>('cont_dashboard'), staleTime: 0 });

  const s = data?.situacion;
  const cuadra = s ? Math.abs(s.activos - (s.pasivos + s.patrimonio + s.resultado)) < 0.5 : true;
  const utilAnio = (data?.resultado.ingresos_anio ?? 0) - (data?.resultado.gastos_anio ?? 0);
  const pend = data?.pendientes;
  const totalPend = pend ? pend.ventas + pend.compras + pend.retenciones + pend.inventario : 0;
  const serie = (data?.serie_mensual ?? []).map((m) => ({ mes: MESES[m.mes - 1], Ingresos: Number(m.ingresos) / escala, Gastos: Number(m.gastos) / escala }));
  const totalMod = (data?.asientos.por_modulo ?? []).reduce((a, m) => a + Number(m.n), 0) || 1;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold">Dashboard · Contabilidad</h1>
          <p className="text-sm text-muted-foreground">Situación, resultados del año {data?.anio ?? ''}, actividad y estado del ejercicio</p>
        </div>
        <div className="flex items-center gap-3">
          {nota && (
            <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary">{nota}</span>
          )}
          <label className="flex flex-col text-xs text-muted-foreground">Escala
            <select className={`${field} mt-1 w-32`} value={escala}
              onChange={(e) => setEscala(Number(e.target.value))}>
              {ESCALAS.map((x) => <option key={x.v} value={x.v}>{x.label}</option>)}
            </select>
          </label>
          {isFetching && <Loader2 className="w-5 h-5 animate-spin text-primary" />}
        </div>
      </div>

      {/* Situación financiera */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={<Building2 className="w-5 h-5" />} label="Activos" valor={$(s?.activos)} />
        <Kpi icon={<Landmark className="w-5 h-5" />} label="Pasivos" valor={$(s?.pasivos)} />
        <Kpi icon={<Wallet className="w-5 h-5" />} label="Patrimonio" valor={$(s?.patrimonio)} />
        <Kpi icon={<Scale className="w-5 h-5" />} label="Resultado del ejercicio" valor={$(s?.resultado)} clase={Number(s?.resultado ?? 0) >= 0 ? 'text-estable' : 'text-urgente'} detalle={cuadra ? 'Ecuación contable cuadra' : '⚠ descuadre'} />
      </div>

      {/* Resultados del período */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={<TrendingUp className="w-5 h-5" />} label="Ingresos del mes" valor={$(data?.resultado.ingresos_mes)} clase="text-estable" />
        <Kpi icon={<TrendingDown className="w-5 h-5" />} label="Gastos del mes" valor={$(data?.resultado.gastos_mes)} clase="text-urgente" />
        <Kpi icon={<BookText className="w-5 h-5" />} label="Asientos" valor={String(data?.asientos.mes ?? 0)} detalle={`${data?.asientos.anio ?? 0} en el año`} />
        <Kpi icon={<Scale className="w-5 h-5" />} label="Utilidad del año" valor={$(utilAnio)} clase={utilAnio >= 0 ? 'text-estable' : 'text-urgente'} detalle={`Ing. ${$(data?.resultado.ingresos_anio)} · Gto. ${$(data?.resultado.gastos_anio)}`} />
      </div>

      {/* Alertas */}
      {(!cuadra || totalPend > 0 || data?.ejercicio.estado === 'sin ejercicio') && (
        <div className="space-y-2">
          {!cuadra && (
            <div className="flex items-center gap-2 rounded-md bg-urgente-soft text-urgente px-3 py-2 text-sm">
              <AlertTriangle className="w-4 h-4" /> La ecuación contable no cuadra (Activo ≠ Pasivo + Patrimonio + Resultado). Revisa los asientos.
            </div>
          )}
          {totalPend > 0 && (
            <button onClick={() => ir('/contabilidad/asientos', 'Asientos (Libro Diario)')} className="w-full text-left flex items-center gap-2 rounded-md bg-pendiente-soft text-pendiente px-3 py-2 text-sm">
              <AlertTriangle className="w-4 h-4" />
              <span>{totalPend} documento(s) de otros módulos sin contabilizar</span>
              <span className="ml-auto text-xs opacity-80">
                {[['ventas', pend!.ventas], ['compras', pend!.compras], ['retenc.', pend!.retenciones], ['inventario', pend!.inventario]].filter(([, n]) => Number(n) > 0).map(([t, n]) => `${n} ${t}`).join(' · ')}
              </span>
            </button>
          )}
          {Number(pend?.errores_contabilizacion) > 0 && (
            <button onClick={() => ir('/contabilidad/periodos', 'Períodos y Cierre')} className="w-full text-left flex items-center gap-2 rounded-md bg-urgente-soft text-urgente px-3 py-2 text-sm">
              <AlertTriangle className="w-4 h-4" />
              <span><b>{pend!.errores_contabilizacion} movimiento(s) de inventario sin asiento por error</b> (p. ej. período cerrado). El stock se registró pero el asiento se rechazó — revisa el período o reabre para contabilizarlos.</span>
            </button>
          )}
          {data?.ejercicio.estado === 'sin ejercicio' && (
            <button onClick={() => ir('/contabilidad/periodos', 'Períodos y Cierre')} className="w-full text-left flex items-center gap-2 rounded-md bg-muted text-muted-foreground px-3 py-2 text-sm">
              <CalendarRange className="w-4 h-4" /> No hay ejercicio fiscal {data?.anio} definido — crea el ejercicio para habilitar el control de períodos.
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Ingresos vs Gastos mensual */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Activity className="w-4 h-4" /> Ingresos vs. gastos {data?.anio}</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={serie} margin={{ left: 8, right: 8 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-muted" />
                <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={compact} tick={{ fontSize: 11 }} width={44} />
                <Tooltip formatter={(v: number) => (escala === 1 ? usd(v) : usdEsc(v * escala, escala))} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Ingresos" fill={cIng} radius={[4, 4, 0, 0]} maxBarSize={22} />
                <Bar dataKey="Gastos" fill={cGas} radius={[4, 4, 0, 0]} maxBarSize={22} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Asientos por módulo */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Layers className="w-4 h-4" /> Asientos por módulo</CardTitle></CardHeader>
          <CardContent>
            {(data?.asientos.por_modulo.length ?? 0) === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">Sin asientos este año</p>
            ) : (
              <div className="space-y-3">
                {data!.asientos.por_modulo.map((m) => (
                  <div key={m.modulo}>
                    <div className="flex justify-between text-sm mb-1">
                      <span>{MODULO_LABEL[m.modulo] ?? m.modulo}</span>
                      <span className="font-mono tabular-nums text-muted-foreground">{m.n}</span>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(Number(m.n) / totalMod) * 100}%`, background: cIng }} /></div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top gastos */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><TrendingDown className="w-4 h-4" /> Mayores gastos del año</CardTitle></CardHeader>
          <CardContent>
            {(data?.top_gastos.length ?? 0) === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">Sin gastos registrados</p>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(160, data!.top_gastos.length * 42)}>
                <BarChart data={data!.top_gastos.map((g) => ({ nombre: g.nombre, monto: Number(g.monto) / escala }))} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} className="stroke-muted" />
                  <XAxis type="number" tickFormatter={compact} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="nombre" width={130} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: number) => (escala === 1 ? usd(v) : usdEsc(v * escala, escala))} />
                  <Bar dataKey="monto" fill={cGas} radius={[0, 4, 4, 0]} barSize={18} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Estado del ejercicio */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><CalendarRange className="w-4 h-4" /> Ejercicio fiscal {data?.ejercicio.anio}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Estado:</span>
              <span className={`px-2 py-0.5 rounded-md text-xs ${data?.ejercicio.estado === 'abierto' ? 'bg-estable-soft text-estable' : 'bg-muted text-muted-foreground'}`}>{data?.ejercicio.estado ?? '—'}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border p-3">
                <div className="text-2xl font-semibold font-mono text-estable">{data?.ejercicio.periodos_abiertos ?? 0}</div>
                <div className="text-xs text-muted-foreground">períodos abiertos</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-2xl font-semibold font-mono">{data?.ejercicio.periodos_cerrados ?? 0}</div>
                <div className="text-xs text-muted-foreground">períodos cerrados</div>
              </div>
            </div>
            <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-md ${cuadra ? 'bg-estable-soft text-estable' : 'bg-urgente-soft text-urgente'}`}>
              {cuadra ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
              {cuadra ? 'La contabilidad cuadra (partida doble).' : 'Hay un descuadre en los saldos.'}
            </div>
            <button onClick={() => ir('/contabilidad/periodos', 'Períodos y Cierre')} className="text-sm text-primary hover:underline">Gestionar períodos y cierre →</button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
